import { AppService } from '@/bundles/App/models/AppService';
import { IPaginationResponse } from '@/bundles/App/interfaces/IPaginationResponse';
import { Nullable } from '@/utils/types';
import PipelineDashboardBreakdownsModel from '@/bundles/Pipeline/models/PipelineDashboardBreakdownsModel';
import PipelineDashboardPipelineDetailsModel from '@/bundles/Pipeline/models/PipelineDashboardPipelineDetailsModel';
import PipelineYearAggregateModel from '@/bundles/Pipeline/models/PipelineYearAggregateModel';
import { PipelinesChartList } from '@/bundles/Visualizations/types';
import { mapPipelineDashboardFilterOptions } from '@/bundles/Pipeline/mappers/mapPipelineDashboardFilterOptions';
import {
  IPipelineDashboardBreakdownsGetOne,
  IPipelineDashboardPipelineDetailsGetOne,
  IPipelineDashboardFilterOptions,
  IPipelineDashboardFilterOptionsGetOne,
  IPipelineDashboardKanbanCardListItem,
  IPipelineDashboardKanbanGetOne,
  IPipelineDashboardKpisGetOne,
  IPipelineYearAggregate,
  PipelineStageGroupEnum,
} from '@/bundles/Pipeline/types';
import {
  IPipelineDashboardFiltersDto,
  IPipelineDashboardKanbanGetDto,
  IPipelineDashboardKanbanStageCardsGetDto,
  IPipelineDashboardKpisGetDto,
  IPipelineDashboardCardStageUpdateDto,
  IPipelineDashboardCardStageUpdateResponse,
} from '@/bundles/Pipeline/dto';

export default class PipelineServiceNest extends AppService {
  async getFilterOptions (): Promise<IPipelineDashboardFilterOptions> {
    try {
      const response: IPipelineDashboardFilterOptionsGetOne = await this.transport.get(`/dashboard/pipeline/filter-options`);
      return mapPipelineDashboardFilterOptions(response);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async getKpis (
    params: IPipelineDashboardKpisGetDto,
    controller: Nullable<AbortController> = null,
  ): Promise<IPipelineDashboardKpisGetOne> {
    try {
      return await this.transport.get(`/dashboard/pipeline/kpis`, params, controller);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async getKanban (
    params: IPipelineDashboardKanbanGetDto,
    controller: Nullable<AbortController> = null,
  ): Promise<IPipelineDashboardKanbanGetOne> {
    try {
      return await this.transport.get(`/dashboard/pipeline/kanban`, params, controller);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async getStageCards (
    group: PipelineStageGroupEnum,
    params: IPipelineDashboardKanbanStageCardsGetDto,
    controller: Nullable<AbortController> = null,
  ): Promise<IPaginationResponse<IPipelineDashboardKanbanCardListItem>> {
    try {
      return await this.transport.get(`/dashboard/pipeline/kanban/stage/${group}/cards`, params, controller);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async getPipelineDetails (
    id: string,
    controller: Nullable<AbortController> = null,
  ): Promise<IPipelineDashboardPipelineDetailsGetOne> {
    try {
      const response: IPipelineDashboardPipelineDetailsGetOne = await this.transport.get(`/dashboard/pipeline/${id}`, {}, controller);
      return new PipelineDashboardPipelineDetailsModel(response);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async getBreakdowns (
    params: IPipelineDashboardFiltersDto,
    controller: Nullable<AbortController> = null,
  ): Promise<IPipelineDashboardBreakdownsGetOne> {
    try {
      const response: IPipelineDashboardBreakdownsGetOne = await this.transport.get('/dashboard/pipeline/breakdowns', params, controller);
      return new PipelineDashboardBreakdownsModel(response);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  /* Chart payload for the on-demand Visualizations section. The backend already speaks the
     Visualizations bundle's own chart/metric vocabulary, so the response drops straight into
     PipelinesBaseChart with no mapping. Partial: the backend has no broker_gross chart. */
  async getVisualizations (
    params: IPipelineDashboardFiltersDto,
    controller: Nullable<AbortController> = null,
  ): Promise<Partial<PipelinesChartList>> {
    try {
      return await this.transport.get('/dashboard/pipeline/visualizations', params, controller);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async getYearFilters (
    params: IPipelineDashboardFiltersDto,
    controller: Nullable<AbortController> = null,
  ): Promise<PipelineYearAggregateModel[]> {
    try {
      const response: IPipelineYearAggregate[] = await this.transport.get('/dashboard/pipeline/year-summary', params, controller);
      return response.map(year => new PipelineYearAggregateModel(year));
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async updateCardStage (
    dto: IPipelineDashboardCardStageUpdateDto,
  ): Promise<IPipelineDashboardCardStageUpdateResponse> {
    try {
      // `filters` are scoped to the active dashboard view so the backend can
      // recompute stage totals against the same filtered dataset and return
      // them in IPipelineDashboardCardStageUpdateResponse.
      return await this.transport.patch(`/dashboard/pipeline/${dto.card_id}/stage`, {
        to_stage: dto.to_stage,
        _rev: dto._rev,
        filters: dto.filters,
      });
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }
}
